import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "../api/axios";
import { useNavigate } from "react-router-dom";
import { frontendAccessRules } from "../utility/accessRules"; // ✅ Import access rules

import { useAnalytics } from "./AnalyticsProvider";


const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
const SESSION_MAX_DURATION = 4 * 60 * 60 * 1000; // 4 hours



const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState({ isAuthenticated: false, user: null });
  const [loading, setLoading] = useState(true); // ✅ Start as true
  const isCheckingSession = useRef(false);
  const sessionStartTime = useRef(Date.now());
  const lastActivityRef = useRef(Date.now());

  const analytics = useAnalytics();



  const logout = useCallback(async () => {
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      if (!csrfToken) {
        console.warn("🚨 Missing CSRF token. Proceeding with frontend logout.");
      } else {
        await axios.post("/api/user/logout", {}, {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken }
        });
      }
  
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("⚠️ Already logged out on the server.");
      } else {
        console.error("Logout error:", error);
      }
    } finally {
      setAuth({ isAuthenticated: false, user: null });
      navigate("/login");
    }
  }, [navigate, setAuth]); // ✅ Dependencies added  

  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
    };
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const inactiveFor = now - lastActivityRef.current;

      if (inactiveFor >= INACTIVITY_LIMIT) {
        console.log("🚨 Logging out due to inactivity.");
        logout();
      }
    }, 30 * 1000); // 30s is good (60s also fine)

    return () => clearInterval(interval);
  }, [auth.isAuthenticated, logout]);


  useEffect(() => {
    const interval = setInterval(() => {
      if (auth.isAuthenticated) {
        const elapsed = Date.now() - sessionStartTime.current;
        if (elapsed >= SESSION_MAX_DURATION) {
          console.log("🚨 Hard session limit reached. Logging out.");
          logout();
        }
      }
    }, 60 * 1000); // ✅ Check every minute
  
    return () => clearInterval(interval);
  }, [auth.isAuthenticated, logout]);
  

  //console.log(`🔄 AuthProvider Rendered at ${location.pathname}`);

  useEffect(() => {
    const checkSession = async () => {
      if (isCheckingSession.current) return; // ✅ Prevents infinite calls
      isCheckingSession.current = true;
  
      try {
        const response = await axios.get("/api/auth/me", { withCredentials: true });
  
        if (response.data.user) {
          setAuth({ isAuthenticated: true, user: response.data.user });
        } else {
          setAuth({ isAuthenticated: false, user: null });
        }
      } catch (error) {
        if (error.response?.status === 401) {
          if (auth.isAuthenticated) {
            await logout(); // ✅ logout is safe because it's wrapped in `useCallback`
          } else {
            setAuth({ isAuthenticated: false, user: null });
          }
        } else {
          console.error("⚠️ Auth check error:", error);
        }
      } finally {
        isCheckingSession.current = false;
        setLoading(false);
      }
    };
  
    if (document.cookie && document.cookie.length > 0) {
      checkSession();
    } else {
      setLoading(false); // Don't leave the app in a "loading" state
    }
  }, [auth.isAuthenticated, logout]); // ✅ Added missing dependencies  
  


    
  

  const hasAccess = (route) => {
    if (!auth.isAuthenticated || !auth.user) {
      console.log(`❌ Access denied (not authenticated) for route: ${route}`);
      return false;
    }
  
    const matchingRule = Object.keys(frontendAccessRules).find((baseRoute) =>
      route.startsWith(baseRoute)
    );
  
    //console.log(`🔍 Checking access for route: ${route}, Found rule: ${matchingRule}`);
  
    if (!matchingRule) {
      console.log(`✅ No specific rule found for ${route}, allowing access.`);
      return true;
    }
  
    const allowedRoles = frontendAccessRules[matchingRule];
    const userHasAccess = allowedRoles.includes(auth.user.role);
  
    //console.log(`👤 User Role: ${auth.user.role}`);
    //console.log(`🔑 Allowed Roles for ${matchingRule}: ${allowedRoles}`);
    //console.log(`🔍 Access ${userHasAccess ? "GRANTED" : "DENIED"} for ${route}`);
  
    return userHasAccess;
  };

  const login = async (credentials) => {
    try {
        const response = await axios.post("/api/auth/login", credentials, { withCredentials: true });
  
        if (response.status === 206) {
            return response.data; // ✅ 2FA required, return response without logging in
        } else if (response.data.user) {
            setAuth({ isAuthenticated: true, user: response.data.user });
            sessionStartTime.current = Date.now();
            lastActivityRef.current = Date.now(); // ✅ ADD THIS

            // ✅ Fire GA4 login event
            analytics.track("login", {
              method: "email_password"
            });
  
            // ✅ Redirect based on role
            if (["admin", "siteadmin"].includes(response.data.user.role)) {
                window.location.href = "/admin/dashboard";
            } else {
                window.location.href = "/user/profile";
            }
  
            return { success: true };
        }
    } catch (error) {
        return { error: error.response?.data?.error || "Login failed. Please try again." };
    }
  };  


  

  
  


  return (
    <AuthContext.Provider value={{ auth, login, logout, hasAccess, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
